import type { CaptainRole } from '../../types/captain'
import { ROLE_LABEL } from '../../types/captain'

type Variant = 'open' | 'met' | 'mine' | 'past'

interface Props {
  role: CaptainRole
  variant: Variant
  /**
   * Freeform meta text rendered next to the role label. Callers decide the
   * audience-appropriate copy — e.g. "2 signed up" for the participant
   * surface (never a denominator/target, ON-334) vs. "2 of 3 needed" or
   * "3 of 3 needed · +1 extra" for the organizer surface. Omit for no meta.
   */
  meta?: string
  compact?: boolean
}

const STYLES: Record<Variant, { bg: string; border: string; text: string; meta: string }> = {
  open: {
    bg: 'transparent',
    border: 'rgba(244,242,242,0.18)',
    text: '#F4F2F2',
    meta: 'rgba(198,196,185,0.55)',
  },
  met: {
    bg: 'rgba(194,229,48,0.08)',
    border: 'rgba(194,229,48,0.45)',
    text: '#C2E530',
    meta: 'rgba(194,229,48,0.75)',
  },
  mine: {
    bg: 'rgba(194,229,48,0.16)',
    border: '#C2E530',
    text: '#C2E530',
    meta: 'rgba(194,229,48,0.85)',
  },
  past: {
    bg: 'transparent',
    border: 'rgba(244,242,242,0.1)',
    text: 'rgba(198,196,185,0.55)',
    meta: 'rgba(198,196,185,0.4)',
  },
}

export default function RoleSlotPill({ role, variant, meta, compact }: Props) {
  const s = STYLES[variant]
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: compact ? '4px 10px' : '6px 12px',
        borderRadius: 999,
        background: s.bg,
        border: `0.5px solid ${s.border}`,
        fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
        fontSize: compact ? 11 : 12,
        fontWeight: 500,
        letterSpacing: '0.04em',
        color: s.text,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      {ROLE_LABEL[role]}
      {meta && (
        <span
          style={{
            fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
            fontSize: compact ? 10 : 11,
            color: s.meta,
            letterSpacing: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {meta}
        </span>
      )}
      {variant === 'mine' && (
        <span
          aria-hidden
          style={{ color: s.meta, fontSize: compact ? 11 : 12, lineHeight: 1 }}
        >
          ✓
        </span>
      )}
    </span>
  )
}
